<template>
  <section class="mb-5">
    <FormulateForm
      ref="taxConfigurationGroupForm"
      name="taxConfigurationGroupForm"
      @submit="save"
    >
      <b-card-actions
        action-collapse
        :title="$t('Informações Gerais')"
        :show-loading="isLoading"
      >
        <b-row>
          <b-col md="4">
            <FormulateInput
              id="tax-configuration-group-name"
              v-model="form.name"
              name="name"
              :type="isEdit ? 'label' : 'text'"
              :label="$t('Nome')"
              :instruction="$t('É por este nome que a exceção é associada ao produto')"
              class="required"
              validation="required"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="tax-configuration-group-ncm"
              v-model="form.ncm"
              name="ncm"
              :type="isEdit ? 'label' : 'select-searchable'"
              :label="$t('NCM')"
              :placeholder="$t('Todos os NCMs')"
              :instruction="
                $t(
                  'Quando informado, na associação com o produto este cadastro só aparecerá se for deste NCM'
                )
              "
              :options="getComboNcms"
              @fetchSearch="ncmSearch"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="tax-configuration-group-active"
              v-model="form.active"
              name="active"
              type="switch"
              :label="$t('Ativo')"
              :disabled="isInUse"
              :instruction="
                isInUse
                  ? $t(
                    'Não é possível inativar: existem produtos associados a este cadastro. Remova a associação pela ação \'Associar produtos\' na listagem de Exceções Fiscais.'
                  )
                  : ''
              "
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <FormulateInput
              id="tax-configuration-group-description"
              v-model="form.description"
              name="description"
              type="textarea"
              rows="2"
              :label="$t('Descrição')"
            />
          </b-col>
        </b-row>
      </b-card-actions>

      <b-card-actions
        action-collapse
        :title="$t('Tributos da reforma (CBS/IBS e Imposto Seletivo)')"
        :show-loading="isLoading"
      >
        <tax-configuration-group-tax-reform-form :busy="isLoading" />
      </b-card-actions>

      <b-card-actions
        action-collapse
        :title="$t('Exceções fiscais por UF')"
        :show-loading="isLoading"
      >
        <tax-configuration-group-item-table
          @add-item="openItemSidebar()"
          @update-item="openItemSidebar"
          @delete-item="deleteItem"
        />
        <tax-configuration-group-item-sidebar
          ref="itemSidebar"
          @create="addItem"
          @update="updateItem"
        />
      </b-card-actions>
    </FormulateForm>

    <e-crud-actions
      :busy="isLoading"
      @save="save"
      @cancel="cancel"
    />
  </section>
</template>

<script>
import { BRow, BCol } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { alerts } from '@/mixins'
import { ECrudActions } from '@/views/components'
import TaxConfigurationGroupItemTable from './components/TaxConfigurationGroupItemTable.vue'
import TaxConfigurationGroupItemSidebar from './components/TaxConfigurationGroupItemSidebar.vue'
import TaxConfigurationGroupTaxReformForm from './components/TaxConfigurationGroupTaxReformForm.vue'

export default {
  name: 'TaxConfigurationGroupMaintain',
  components: {
    BRow,
    BCol,
    BCardActions,
    ECrudActions,
    TaxConfigurationGroupItemTable,
    TaxConfigurationGroupItemSidebar,
    TaxConfigurationGroupTaxReformForm,
  },
  mixins: [alerts],

  data() {
    return {
      busy: false,
      saving: false,
      isEdit: !!this.$router.currentRoute.params.id,
    }
  },

  computed: {
    ...mapState('pages/invoice/taxConfigurationGroups/taxConfigurationGroupMaintain', [
      'taxConfigurationGroup',
    ]),
    ...mapGetters('pages/invoice/taxConfigurationGroups/taxConfigurationGroupMaintain', [
      'itemsForSave',
      'isInUse',
      'taxReformForSave',
    ]),
    ...mapGetters('common/ncms', ['getComboNcms']),

    // O formulário edita o cabeçalho direto no store, como nas demais telas fiscais: o sidebar
    // das linhas precisa enxergar o NCM escolhido para pré-preencher a partir dele.
    form() {
      return this.taxConfigurationGroup
    },

    isLoading() {
      return this.busy || this.saving
    },
  },

  async mounted() {
    try {
      this.busy = true
      this.cleanState()

      if (this.isEdit) {
        await this.fetchTaxConfigurationGroupById(this.$router.currentRoute.params.id)

        // O combo de NCM é carregado por busca; sem isso o campo abriria vazio na edição.
        if (this.form.ncm) await this.fetchNcms({ itemSearch: this.form.ncm })
      }
    } catch (error) {
      this.showGenericError({ error })
    } finally {
      this.busy = false
    }
  },

  methods: {
    ...mapActions('common/ncms', ['fetchNcms']),
    ...mapActions('pages/invoice/taxConfigurationGroups/taxConfigurationGroupMaintain', [
      'fetchTaxConfigurationGroupById',
      'addItem',
      'updateItem',
      'deleteItem',
      'cleanState',
    ]),

    async ncmSearch(loading, search) {
      try {
        loading(true)
        await this.fetchNcms({ itemSearch: search })
      } catch (e) {
        this.showError({ message: this.$t('Não foi possível fazer a pesquisa') })
      } finally {
        loading(false)
      }
    },

    openItemSidebar(item) {
      this.$refs.itemSidebar.show(item)
    },

    async save() {
      if (await this.$refs.taxConfigurationGroupForm.hasValidationErrors()) {
        this.$refs.taxConfigurationGroupForm.showErrors()
        this.showInvalidDataMessage()
        return
      }

      try {
        this.saving = true

        const { new: newItems, updated: updatedItems, deleted: deletedItems } = this.itemsForSave
        // localId é controle de lista do front e não existe no contrato da API.
        const normalize = ({ localId, isDeleted, ...item }) => ({
          ...item,
          state: item.state || null,
          destinationState: item.destinationState || '',
        })

        const header = {
          name: this.form.name,
          description: this.form.description,
          ncm: this.form.ncm || null,
          active: this.form.active !== false,
          ...this.taxReformForSave,
        }

        if (this.isEdit) {
          await this.$http.put('/api/tax/tax-configuration-groups', {
            ...header,
            id: this.form.id,
            new: newItems.map(normalize),
            updated: updatedItems.map(normalize),
            deleted: deletedItems.map(item => item.id),
          })
        } else {
          await this.$http.post('/api/tax/tax-configuration-groups', {
            ...header,
            items: newItems.map(normalize),
          })
        }

        this.showSuccess({
          message: this.$t(`${this.isEdit ? 'Atualização' : 'Inclusão'} realizada com sucesso`),
        })
        this.cleanState()
        this.$router.push({ name: 'tax-configuration-group-list' })
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    cancel() {
      this.cleanState()
      this.$router.push({ name: 'tax-configuration-group-list' })
    },
  },
}
</script>
